import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Activity,
  MapPin,
  Calendar,
  Target,
  Zap,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface PerformanceTrend {
  site: string;
  currentScore: number;
  previousScore: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
}

interface CompetitorInsight {
  county: string;
  competitors: number;
  avgRating: number;
  topRated: { name: string; rating: number } | null;
}

interface Alert {
  type: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  action?: string;
}

interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  category: string;
  title: string;
  description: string;
  impact: string;
}

export default function AnalyticsInsightsPage() {
  const [performanceTrends, setPerformanceTrends] = useState<PerformanceTrend[]>([]);
  const [competitorInsights, setCompetitorInsights] = useState<CompetitorInsight[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  async function loadAnalytics() {
    setLoading(true);
    try {
      await Promise.all([
        loadPerformanceTrends(),
        loadCompetitorInsights(),
        generateAlerts(),
        generateRecommendations(),
      ]);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadPerformanceTrends() {
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

    const { data: vitals } = await supabase
      .from('core_web_vitals')
      .select('site_url, site_name, performance_score, checked_at')
      .gte('checked_at', cutoffDate.toISOString())
      .order('checked_at', { ascending: false });

    if (!vitals || vitals.length === 0) {
      setPerformanceTrends([]);
      return;
    }

    // Group by site and calculate trends
    const siteMap = new Map<string, any[]>();
    vitals.forEach(v => {
      if (!siteMap.has(v.site_url)) {
        siteMap.set(v.site_url, []);
      }
      siteMap.get(v.site_url)!.push(v);
    });

    const trends: PerformanceTrend[] = [];
    siteMap.forEach((records, siteUrl) => {
      if (records.length < 2) return;

      const current = records[0];
      const previous = records[Math.min(records.length - 1, 5)]; // Compare with 5th most recent or oldest
      const change = current.performance_score - previous.performance_score;

      trends.push({
        site: current.site_name || siteUrl,
        currentScore: current.performance_score,
        previousScore: previous.performance_score,
        change: Math.round(change),
        trend: change > 2 ? 'up' : change < -2 ? 'down' : 'stable',
      });
    });

    setPerformanceTrends(trends);
  }

  async function loadCompetitorInsights() {
    const { data: competitors } = await supabase
      .from('competitor_locations')
      .select('county, name, rating, total_ratings');

    if (!competitors) {
      setCompetitorInsights([]);
      return;
    }

    // Group by county
    const countyMap = new Map<string, any[]>();
    competitors.forEach(c => {
      if (!countyMap.has(c.county)) {
        countyMap.set(c.county, []);
      }
      countyMap.get(c.county)!.push(c);
    });

    const insights: CompetitorInsight[] = [];
    countyMap.forEach((comps, county) => {
      const rated = comps.filter(c => c.rating > 0);
      const avgRating = rated.length > 0
        ? rated.reduce((sum, c) => sum + c.rating, 0) / rated.length
        : 0;

      const topRated = rated.length > 0
        ? rated.reduce((max, c) => c.rating > max.rating ? c : max)
        : null;

      insights.push({
        county,
        competitors: comps.length,
        avgRating: Math.round(avgRating * 10) / 10,
        topRated: topRated ? { name: topRated.name, rating: topRated.rating } : null,
      });
    });

    setCompetitorInsights(insights.sort((a, b) => b.competitors - a.competitors));
  }

  async function generateAlerts() {
    const newAlerts: Alert[] = [];

    // Check for poor performance scores
    const { data: recentVitals } = await supabase
      .from('core_web_vitals')
      .select('site_name, performance_score, lcp, checked_at')
      .order('checked_at', { ascending: false })
      .limit(10);

    if (recentVitals) {
      const poorPerformers = recentVitals.filter(v => v.performance_score < 50);
      if (poorPerformers.length > 0) {
        newAlerts.push({
          type: 'critical',
          title: 'Critical Performance Issues Detected',
          description: `${poorPerformers.length} site(s) scoring below 50. Immediate optimization needed.`,
          action: 'View Web Vitals Dashboard',
        });
      }

      const slowLCP = recentVitals.filter(v => v.lcp > 2500);
      if (slowLCP.length > 0) {
        newAlerts.push({
          type: 'warning',
          title: 'Slow Page Load Times',
          description: `${slowLCP.length} site(s) with LCP > 2.5s. User experience may be impacted.`,
          action: 'Optimize Images & Resources',
        });
      }
    }

    // Check competitor density
    if (competitorInsights.length > 0) {
      const highCompetition = competitorInsights.filter(c => c.competitors > 50);
      if (highCompetition.length > 0) {
        newAlerts.push({
          type: 'info',
          title: 'High Competition Markets',
          description: `${highCompetition.map(c => c.county).join(', ')} have 50+ competitors. Focus on differentiation.`,
        });
      }
    }

    setAlerts(newAlerts);
  }

  async function generateRecommendations() {
    const recs: Recommendation[] = [];

    // Performance-based recommendations
    if (performanceTrends.length > 0) {
      const declining = performanceTrends.filter(t => t.trend === 'down');
      if (declining.length > 0) {
        recs.push({
          priority: 'high',
          category: 'Performance',
          title: 'Address Performance Degradation',
          description: `${declining.map(d => d.site).join(', ')} showing declining scores. Review recent changes and optimize resources.`,
          impact: 'Improved user experience, higher conversion rates',
        });
      }

      const improving = performanceTrends.filter(t => t.trend === 'up');
      if (improving.length > 0) {
        recs.push({
          priority: 'low',
          category: 'Performance',
          title: 'Maintain Performance Gains',
          description: `${improving.map(d => d.site).join(', ')} showing improvement. Document what's working and apply to other sites.`,
          impact: 'Consistent user experience across all properties',
        });
      }
    }

    // Competitor-based recommendations
    if (competitorInsights.length > 0) {
      const highRated = competitorInsights.filter(c => c.avgRating > 4.5);
      if (highRated.length > 0) {
        recs.push({
          priority: 'high',
          category: 'Competition',
          title: 'Match High-Performing Markets',
          description: `Competitors in ${highRated.map(c => c.county).join(', ')} averaging 4.5+ stars. Focus on review generation and service quality.`,
          impact: 'Improved local rankings, increased customer trust',
        });
      }

      const lowCompetition = competitorInsights.filter(c => c.competitors < 30);
      if (lowCompetition.length > 0) {
        recs.push({
          priority: 'medium',
          category: 'Opportunity',
          title: 'Expand in Underserved Markets',
          description: `${lowCompetition.map(c => c.county).join(', ')} have fewer competitors. Consider expanding marketing efforts.`,
          impact: 'Market share growth, easier customer acquisition',
        });
      }
    }

    // General recommendations
    recs.push({
      priority: 'medium',
      category: 'Monitoring',
      title: 'Set Up Automated Daily Collection',
      description: 'Schedule automated data collection to track trends and catch issues early.',
      impact: 'Proactive issue detection, continuous improvement',
    });

    setRecommendations(recs.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics & Insights</h1>
          <p className="text-gray-600">
            AI-powered analysis of your market intelligence data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-8 space-y-4">
          {alerts.map((alert, index) => (
            <div
              key={index}
              className={`border-l-4 p-4 rounded-lg ${
                alert.type === 'critical'
                  ? 'bg-red-50 border-red-500'
                  : alert.type === 'warning'
                  ? 'bg-yellow-50 border-yellow-500'
                  : 'bg-blue-50 border-blue-500'
              }`}
            >
              <div className="flex items-start gap-3">
                {alert.type === 'critical' ? (
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                ) : alert.type === 'warning' ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{alert.title}</h3>
                  <p className="text-sm text-gray-700 mt-1">{alert.description}</p>
                  {alert.action && (
                    <p className="text-sm text-primary-600 font-medium mt-2">
                      → {alert.action}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Performance Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Performance Trends
          </h2>
          {performanceTrends.length > 0 ? (
            <div className="space-y-4">
              {performanceTrends.map((trend) => (
                <div key={trend.site} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{trend.site}</p>
                    <p className="text-sm text-gray-500">
                      Score: {trend.currentScore} (was {trend.previousScore})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {trend.trend === 'up' ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-medium">+{trend.change}</span>
                      </div>
                    ) : trend.trend === 'down' ? (
                      <div className="flex items-center gap-1 text-red-600">
                        <TrendingDown className="w-5 h-5" />
                        <span className="font-medium">{trend.change}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-500">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Stable</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Not enough data for trend analysis. Collect more data points.
            </p>
          )}
        </div>

        {/* Competitor Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Competitive Landscape
          </h2>
          {competitorInsights.length > 0 ? (
            <div className="space-y-4">
              {competitorInsights.map((insight) => (
                <div key={insight.county} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900">{insight.county} County</p>
                    <span className="text-sm text-gray-600">
                      {insight.competitors} competitors
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Average Rating: <span className="font-medium">{insight.avgRating} ⭐</span></p>
                    {insight.topRated && (
                      <p className="mt-1">
                        Top Rated: <span className="font-medium">{insight.topRated.name}</span> ({insight.topRated.rating} ⭐)
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No competitor data available. Run a county scan to get started.
            </p>
          )}
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5" />
          Actionable Recommendations
        </h2>
        <div className="space-y-4">
          {recommendations.map((rec, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start gap-3">
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    rec.priority === 'high'
                      ? 'bg-red-100 text-red-800'
                      : rec.priority === 'medium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {rec.priority.toUpperCase()}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {rec.category}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{rec.title}</h3>
                  <p className="text-sm text-gray-700 mb-2">{rec.description}</p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Expected Impact:</span> {rec.impact}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
